import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import ClientsPage from "./pages/ClientsPage";
import FlottePage from "./pages/FlottePage";
import RepairsPage from "./pages/RepairsPage";
import RepairDetailPage from "./pages/RepairDetailPage";
import InventoryPage from "./pages/InventoryPage";
import InvoicesPage from "./pages/InvoicesPage";
import ReviewsPage from "./pages/ReviewsPage";
import EmployeesPage from "./pages/EmployeesPage";
import ActivityPage from "./pages/ActivityPage";
import TrackingPage from "./pages/TrackingPage";
import ReviewsShowcasePage from "./pages/ReviewsShowcasePage";
import IntakeRequestsPage from "./pages/IntakeRequestsPage";
import PartsToOrderPage from "./pages/PartsToOrderPage";
import MobileUploadPage from "./pages/MobileUploadPage";
import MobileSignaturePage from "./pages/MobileSignaturePage";
import AppointmentsPage from "./pages/AppointmentsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import DeliveryPage from "./pages/DeliveryPage";
import QuotesPage from "./pages/QuotesPage";
import PublicOrderPage from "./pages/PublicOrderPage";
import PublicQuotePage from "./pages/PublicQuotePage";
import OrdersPage from "./pages/OrdersPage";
import ShopSettingsPage from "./pages/ShopSettingsPage";
import HorairesPage from "./pages/HorairesPage";
import PrestationsPage from "./pages/PrestationsPage";
import CashRegisterPage from "./pages/CashRegisterPage";
import ProfilePage from "./pages/ProfilePage";
import IntakeProposalPage from "./pages/IntakeProposalPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

// Protected Route wrapper
// Returns the first route the user has access to (avoids infinite redirect loop)
function getFirstAllowedRoute(user) {
  if (!user) return "/login";
  if (user.role === "admin") return "/";
  const perms = user.permissions || [];
  if (perms.includes("dashboard")) return "/";
  if (perms.includes("repairs")) return "/repairs";
  if (perms.includes("clients")) return "/clients";
  if (perms.includes("inventory")) return "/inventory";
  if (perms.includes("invoices")) return "/invoices";
  if (perms.includes("reviews")) return "/admin/reviews";
  return "/no-access";
}

const ProtectedRoute = ({ children, permission }) => {
  const { user, loading, hasPermission } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Chargement...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (permission && !hasPermission(permission)) {
    const fallback = getFirstAllowedRoute(user);
    // Avoid redirecting to the same route (infinite loop guard)
    if (fallback === window.location.pathname) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center flex-col gap-4">
          <p className="text-muted-foreground text-lg">Accès refusé</p>
          <p className="text-muted-foreground text-sm">Vous n'avez pas les permissions nécessaires.</p>
          <p className="text-muted-foreground text-xs">Contactez votre administrateur.</p>
        </div>
      );
    }
    return <Navigate to={fallback} replace />;
  }
  
  return children;
};

// Admin Route wrapper
const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Chargement...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isAdmin()) {
    const fallback = getFirstAllowedRoute(user);
    return <Navigate to={fallback} replace />;
  }
  
  return children;
};

// Public Route wrapper (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Chargement...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Redirects to the first page the logged-in user has access to
function SmartRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={getFirstAllowedRoute(user)} replace />;
}

const RDV_URL = "https://www.amcphone-martinique.fr/prise-en-charge";

function RdvRedirect() {
  useEffect(() => { window.location.replace(RDV_URL); }, []);
  return (
    <p style={{ padding: 24, fontFamily: "system-ui" }}>
      Redirection vers la prise de rendez-vous… <a href={RDV_URL}>continuer</a>
    </p>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
      <Route path="/delivery" element={<ProtectedRoute permission="repairs"><DeliveryPage /></ProtectedRoute>} />
      <Route path="/intake-proposal/:requestId" element={<IntakeProposalPage />} />
      <Route path="/suivi-commande/:token" element={<PublicOrderPage />} />
      <Route path="/devis/:token" element={<PublicQuotePage />} />
      <Route path="/orders" element={<ProtectedRoute permission="dashboard"><OrdersPage /></ProtectedRoute>} />
      <Route path="/quotes" element={<ProtectedRoute permission="dashboard"><QuotesPage /></ProtectedRoute>} />
      <Route path="/cash" element={<ProtectedRoute permission="dashboard"><CashRegisterPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute permission="dashboard"><ProfilePage /></ProtectedRoute>} />
      <Route path="/admin/shop-settings" element={<AdminRoute><ShopSettingsPage /></AdminRoute>} />
      <Route path="/admin/horaires" element={<AdminRoute><HorairesPage /></AdminRoute>} />
      <Route path="/admin/prestations" element={<AdminRoute><PrestationsPage /></AdminRoute>} />
      
      {/* Public Tracking Page - No Auth Required */}
      <Route path="/suivi/:ticketNumber" element={<TrackingPage />} />
      
      {/* Un seul formulaire de RDV : celui du site. L'app redirige pour qu'il n'existe
          jamais deux calendriers en parallèle. Les anciens liens restent valables. */}
      <Route path="/intake" element={<RdvRedirect />} />
      <Route path="/prise-en-charge" element={<RdvRedirect />} />
      
      {/* Public Reviews Showcase Page */}
      <Route path="/avis" element={<ReviewsShowcasePage />} />
      <Route path="/reviews" element={<ReviewsShowcasePage />} />
      
      {/* Protected Routes */}
      <Route path="/" element={<ProtectedRoute permission="dashboard"><DashboardPage /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute permission="clients"><ClientsPage /></ProtectedRoute>} />
      <Route path="/flotte" element={<ProtectedRoute permission="clients"><FlottePage /></ProtectedRoute>} />
      <Route path="/repairs" element={<ProtectedRoute permission="repairs"><RepairsPage /></ProtectedRoute>} />
      <Route path="/repairs/:id" element={<ProtectedRoute permission="repairs"><RepairDetailPage /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute permission="inventory"><InventoryPage /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute permission="invoices"><InvoicesPage /></ProtectedRoute>} />
      <Route path="/admin/reviews" element={<ProtectedRoute permission="reviews"><ReviewsPage /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin/employees" element={<AdminRoute><EmployeesPage /></AdminRoute>} />
      <Route path="/admin/activity" element={<AdminRoute><ActivityPage /></AdminRoute>} />
      <Route path="/admin/intake-requests" element={<AdminRoute><IntakeRequestsPage /></AdminRoute>} />
      <Route path="/admin/parts-to-order" element={<ProtectedRoute permission="inventory"><PartsToOrderPage /></ProtectedRoute>} />
      <Route path="/admin/appointments" element={<ProtectedRoute permission="repairs"><AppointmentsPage /></ProtectedRoute>} />
      
      {/* Mobile Upload Page - Public */}
      <Route path="/mobile-upload/:token" element={<MobileUploadPage />} />
      
      {/* Mobile Signature Page - Public */}
      <Route path="/mobile-signature/:token" element={<MobileSignaturePage />} />
      
      {/* Fallback */}
      <Route path="/no-access" element={
        <div className="min-h-screen bg-background flex items-center justify-center flex-col gap-4">
          <p className="text-foreground text-xl font-semibold">Accès refusé</p>
          <p className="text-muted-foreground text-sm">Vous n'avez pas les permissions pour accéder à cette section.</p>
          <p className="text-muted-foreground text-xs">Contactez votre administrateur.</p>
        </div>
      } />
      <Route path="*" element={<SmartRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
